import { useEffect, useState } from "react";
import Box from "@mui/material/Box";
import Chip from "@mui/material/Chip";
import IconButton from "@mui/material/IconButton";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import Skeleton from "@mui/material/Skeleton";
import { alpha, useTheme } from "@mui/material/styles";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import CalendarTodayOutlinedIcon from "@mui/icons-material/CalendarTodayOutlined";
import CollectionsOutlinedIcon from "@mui/icons-material/CollectionsOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import { useTranslation } from "react-i18next";
import MDTypography from "../../components/MDTypography";
import EditableTitle from "components/EditableTitle";
import presentationPlaceholder from "../../assets/presntationBG/placeholder.png";
import SlideMiniPreview from "./SlideMiniPreview";
import {
  formatPresentationTimestamp,
  getPresentationPreview,
} from "./presentationPreview";

const EMPTY_TILES = [0, 1, 2, 3].map(() => ({ elements: [], empty: true }));

/**
 * Presentation list card: 2×2 composite of the first four slides
 * (images + text). Falls back to the shared placeholder when empty.
 */
export default function PresentationCard({
  subtitle,
  title,
  isViewingOtherProf = false,
  onOpen,
  onRename,
  onDelete,
}) {
  const { t } = useTranslation();
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [loading, setLoading] = useState(true);
  const [slideCount, setSlideCount] = useState(0);
  const [tiles, setTiles] = useState(EMPTY_TILES);

  const createdAt = subtitle?.created_at ?? subtitle?.createdAt ?? null;
  const updatedAt = subtitle?.updated_at ?? subtitle?.updatedAt ?? createdAt;
  const isPublished = subtitle?.is_published === true;

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    getPresentationPreview(subtitle?.id)
      .then((result) => {
        if (cancelled) return;
        setSlideCount(result.slideCount || 0);
        setTiles(Array.isArray(result.tiles) ? result.tiles : EMPTY_TILES);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [subtitle?.id]);

  const hasAnyContent = tiles.some((tile) => tile && !tile.empty);
  const showComposite = !loading && hasAnyContent;
  const showPlaceholder = !loading && !hasAnyContent;

  const cardBg =
    theme.palette.background?.card ??
    theme.palette.background?.paper ??
    "#fff";
  const softBorder = isDark
    ? alpha(theme.palette.divider, 0.5)
    : "rgba(148,163,184,0.28)";

  return (
    <Box
      onClick={onOpen}
      sx={{
        width: "100%",
        height: "100%",
        display: "flex",
        flexDirection: "column",
        bgcolor: cardBg,
        borderRadius: 2.5,
        border: `1px solid ${softBorder}`,
        overflow: "hidden",
        cursor: "pointer",
        boxShadow: isDark ? "none" : "0 1px 3px rgba(15,23,42,0.04)",
        transition: "box-shadow 0.2s ease, transform 0.2s ease, border-color 0.2s ease",
        "&:hover": {
          boxShadow: isDark
            ? `0 0 0 1px ${alpha(theme.palette.primary.main, 0.35)}`
            : "0 8px 24px rgba(15,23,42,0.08)",
          borderColor: isDark
            ? alpha(theme.palette.primary.main, 0.4)
            : alpha(theme.palette.primary.main, 0.25),
          transform: "translateY(-2px)",
        },
      }}
    >
      {/* Thumbnail */}
      <Box
        sx={{
          position: "relative",
          width: "100%",
          aspectRatio: "16 / 10",
          bgcolor: "#fff",
          overflow: "hidden",
        }}
      >
        {loading && (
          <Skeleton
            variant="rectangular"
            animation="wave"
            sx={{ width: "100%", height: "100%", transform: "none" }}
          />
        )}

        {showPlaceholder && (
          <Box
            sx={{
              width: "100%",
              height: "100%",
              bgcolor: "#fff",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <Box
              component="img"
              src={presentationPlaceholder}
              alt=""
              sx={{
                width: "70%",
                height: "70%",
                objectFit: "contain",
                display: "block",
              }}
            />
          </Box>
        )}

        {showComposite && (
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr",
              gridTemplateRows: "1fr 1fr",
              width: "100%",
              height: "100%",
              gap: "1px",
              bgcolor: isDark ? "rgba(255,255,255,0.08)" : "rgba(15,23,42,0.1)",
            }}
          >
            {tiles.map((tile, idx) => (
              <Box
                key={idx}
                sx={{
                  position: "relative",
                  overflow: "hidden",
                  bgcolor: "#fff",
                  minHeight: 0,
                  minWidth: 0,
                }}
              >
                <SlideMiniPreview
                  elements={tile?.elements}
                  empty={tile?.empty}
                />
              </Box>
            ))}
          </Box>
        )}

        {/* Slide count pill */}
        {!loading && (
          <Box
            sx={{
              position: "absolute",
              left: 10,
              bottom: 10,
              display: "inline-flex",
              alignItems: "center",
              gap: 0.6,
              px: 1.1,
              py: 0.45,
              borderRadius: 999,
              bgcolor: "rgba(15, 23, 42, 0.72)",
              color: "#fff",
              backdropFilter: "blur(6px)",
              zIndex: 1,
            }}
          >
            <CollectionsOutlinedIcon sx={{ fontSize: 14, opacity: 0.95 }} />
            <MDTypography
              variant="caption"
              sx={{ color: "#fff", fontWeight: 600, lineHeight: 1, fontSize: "0.7rem" }}
            >
              {t("pages.presentationTitles.slideCount", { count: slideCount })}
            </MDTypography>
          </Box>
        )}

        {/* Kebab */}
        {!isViewingOtherProf && (
          <IconButton
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              setMenuAnchor(e.currentTarget);
            }}
            sx={{
              position: "absolute",
              top: 6,
              right: 6,
              zIndex: 2,
              bgcolor: "rgba(255,255,255,0.88)",
              color: "text.secondary",
              width: 30,
              height: 30,
              boxShadow: "0 1px 4px rgba(15,23,42,0.12)",
              "&:hover": { bgcolor: "#fff", color: "text.primary" },
            }}
          >
            <MoreVertIcon sx={{ fontSize: 18 }} />
          </IconButton>
        )}
      </Box>

      {/* Metadata */}
      <Box
        sx={{
          px: 1.75,
          pt: 1.5,
          pb: 1.75,
          display: "flex",
          flexDirection: "column",
          gap: 0.75,
          flex: 1,
        }}
      >
        <Box
          onClick={(e) => e.stopPropagation()}
          onMouseDown={(e) => e.stopPropagation()}
          sx={{ minWidth: 0 }}
        >
          {isViewingOtherProf ? (
            <MDTypography
              variant="subtitle1"
              fontWeight={700}
              noWrap
              sx={{ letterSpacing: "-0.01em", lineHeight: 1.3 }}
            >
              {title}
            </MDTypography>
          ) : (
            <EditableTitle
              title={title}
              size={15}
              titleFont="title"
              fullWidth
              align="left"
              onEdit={onRename}
            />
          )}
        </Box>

        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 1,
            mt: "auto",
          }}
        >
          <Box
            sx={{
              display: "inline-flex",
              alignItems: "center",
              gap: 0.6,
              minWidth: 0,
              color: "text.secondary",
            }}
          >
            <CalendarTodayOutlinedIcon sx={{ fontSize: 13, opacity: 0.75 }} />
            <MDTypography
              variant="caption"
              color="text.secondary"
              noWrap
              sx={{ fontSize: "0.72rem" }}
            >
              {formatPresentationTimestamp(updatedAt)}
            </MDTypography>
          </Box>
          <Chip
            size="small"
            label={isPublished ? t("common.published") : t("common.unpublished")}
            sx={{
              fontWeight: 600,
              fontSize: "0.68rem",
              height: 24,
              flexShrink: 0,
              ...(isPublished
                ? {
                    bgcolor: alpha(theme.palette.success.main, 0.12),
                    color: theme.palette.success.dark,
                  }
                : {
                    bgcolor: isDark ? "rgba(255,255,255,0.08)" : "#F1F5F9",
                    color: "text.primary",
                  }),
            }}
          />
        </Box>
      </Box>

      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={() => setMenuAnchor(null)}
        onClick={(e) => e.stopPropagation()}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <MenuItem
          onClick={() => {
            setMenuAnchor(null);
            onDelete?.();
          }}
          sx={{ color: "error.main", gap: 1 }}
        >
          <DeleteOutlineIcon fontSize="small" />
          {t("common.delete")}
        </MenuItem>
      </Menu>
    </Box>
  );
}
